import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
  
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { ConversationType } from "@/constants/chat.constant";
import {
  NAVIGATION_ITEMS,
  ADMIN_PANEL_NAVIGATION_ITEMS,
} from "@/constants/navigation.constant";
import { useUserGroups } from "@/context/user-groups.context";

/**
 * Props for the NavigationMenu component
 */
interface NavigationMenuProps {
  /** Current conversation type */
  conversationType?: ConversationType;
  /** Callback fired when navigation item is clicked */
  onNavigationClick: (url: string, type?: ConversationType) => void;
  /** Whether user is admin. Defaults to false. */
  isAdmin?: boolean;
  /** Whether response is generating. Defaults to false. */
  isGenerating?: boolean;
}

/**
 * NavigationMenu component displays a collapsible navigation menu with conversation
 * type options. Shows different items for admin and regular users. Filters out
 * PolicyGPT in production environment. Highlights active conversation type.
 * 
 * @component
 * @param {NavigationMenuProps} props - Component props
 * @param {ConversationType} [props.conversationType] - Current conversation type
 * @param {(url: string, type?: ConversationType) => void} props.onNavigationClick - Navigation click callback
 * @param {boolean} [props.isAdmin=false] - Whether user is admin
 * @param {boolean} [props.isGenerating] - Whether response is generating
 * @returns {JSX.Element} Navigation menu with conversation type options
 * 
 * @example
 * ```tsx
 * <NavigationMenu
 *   conversationType={ConversationType.STANDARD}
 *   onNavigationClick={handleNavigation}
 *   isAdmin={false}
 * />
 * ```
 */
export function NavigationMenu({
  conversationType,
  onNavigationClick,
  isAdmin = false,
  isGenerating
}: NavigationMenuProps) {

  const { canAccessChopGpt, canAccessPolicyAssistant } = useUserGroups();

  const items = useMemo(
    () => {
      let navigation = []
      const env = import.meta.env.VITE_ENV;
      if (env === 'dev' || env === 'uat') {
        // Developers see all tabs in non-prod environments
        navigation = NAVIGATION_ITEMS;
      } else {
        // Prod: show tabs based on group membership
        navigation = NAVIGATION_ITEMS.filter(({ type }) => {
          if (type === ConversationType.CHOP_GPT) return canAccessChopGpt;
          if (type === ConversationType.POLICY_ASSISTANT) return canAccessPolicyAssistant;
          return true;
        });
      }
      return (isAdmin ? ADMIN_PANEL_NAVIGATION_ITEMS : navigation)
    },
    [isAdmin, canAccessChopGpt, canAccessPolicyAssistant]
  );

  const location = useLocation();

  const isItemSelected = (item: any) =>
    isAdmin
      ? location.pathname.startsWith(item.navigation)
      : conversationType === item?.type;

  const handleNavigationClick = (item: any) =>
    !isGenerating && onNavigationClick(item.navigation, item.type);

  const renderItem = (item: any) => {
    const selectedClass = isItemSelected(item)
      ? "bg-primary-500/10 border border-[#1452B8] rounded-md text-primary-500"
      : "";

    if (item.children?.length) {
      // Collapsible section for parent with children
      return (
        <Collapsible key={item.title} defaultOpen={false} className="group/collapsible">
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton
                className={`flex justify-between w-full ${selectedClass}`}
              >
                <span className="flex items-center gap-2">
                  <span className={`inline-flex items-center justify-center h-6 w-6 rounded ${item.iconColor ?? ''}`}>
              <item.icon className={`h-4 w-4 ${item.iconColor ? 'text-white' : 'text-primary-500'}`} />
                  </span>
                  <span>{item.title}</span>
                </span>
                <ChevronDown
                  className="h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-180"
                />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {item.children.map((child: any) =>
                  renderItem(child)
                )}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    // button for an item
    return (
      <SidebarMenuItem key={item.title}>
        <SidebarMenuButton
          asChild
          className={`${selectedClass}`}
        >
          <button
            onClick={() => handleNavigationClick(item)}
            className="w-full text-sm flex items-center gap-2"
          >
            <span className={`inline-flex items-center justify-center h-6 w-6 rounded ${item.iconColor ?? ''}`}>
                    <item.icon className={`h-4 w-4 ${item.iconColor ? 'text-white' : 'text-primary-500'}`} />
            </span>
            <span>{item.title}</span>
          </button>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-text-200 text-xs">
        {isAdmin ? "Pages" : "Tools"}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => renderItem(item))}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
